import { useMemo } from 'react';

import { selectors, useAppSelector } from 'state/global-state';
import { IOffersState } from 'state/global-state/models/loyalty/offers/offers.types';

import { useServiceOffers } from '../state/service-offers-context';

import { useEnableLoyaltyServiceOffers } from './use-enable-loyalty-service-offers';

/**
 * Single flag-aware source for the applied-offer metadata array. Keep the flag
 * branch here so consumers read one source instead of each re-implementing the
 * gate; do not fold it into `useLoyaltyOffersMap`.
 *
 * Deliberately excludes eviction membership/readiness signals: eviction must
 * read `useServiceOffers().appliedOffersBatch` directly so membership keys on the
 * settled batch-response id set, not this metadata map (which can lag or omit ids
 * while loading).
 */
export function useAppliedOfferMetadata(): IOffersState['cmsOffers'] {
  const { enabled } = useEnableLoyaltyServiceOffers();
  const cmsOffers = useAppSelector(selectors.loyalty.selectCmsOffers);
  const { appliedOffersBatch } = useServiceOffers();

  return useMemo(() => {
    if (!enabled) {
      return cmsOffers;
    }

    // The last settle's batch metadata, held across the null-on-refetch
    // window so applied-offer metadata never momentarily empties.
    return Object.values(appliedOffersBatch.lastSettledMetadataById);
  }, [enabled, appliedOffersBatch.lastSettledMetadataById, cmsOffers]);
}
